import { ReactComponent as PlayerBackward } from '@assets/components/button/icPlayer-backward.svg';
import { ReactComponent as PlayerForward } from '@assets/components/button/icPlayer-forward.svg';
import { ReactComponent as PlayerPause } from '@assets/components/button/icPlayer-pause.svg';
import { ReactComponent as PlayerPlay } from '@assets/components/button/icPlayer-play.svg';
import { ReactComponent as SpeedX1 } from '@assets/components/button/icPlayer-speed-x1.svg';
import { ReactComponent as SpeedX2 } from '@assets/components/button/icPlayer-speed-x2.svg';
import { ReactComponent as SpeedX4 } from '@assets/components/button/icPlayer-speed-x4.svg';
import { ReactComponent as SpeedX6 } from '@assets/components/button/icPlayer-speed-x6.svg';
import { ReactComponent as PlayerStop } from '@assets/components/button/icPlayer-stop.svg';
import { Center, createListCollection, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { CustomSelect } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveRoundConfigurations } from '@redux/config/config.selectors';
import { selectMonitoringSensorBySensorUniqueKey } from '@redux/situation/monitoring.selectors';
import { selectRounds } from '@redux/situation/situation.selectors';
import {
  useGotToSectionMutation,
  useToggleRoundDirectionMutation,
  useUpdateRoundModeMutation,
  useUpdateRoundSpeedMutation,
  useUpdateRoundStatusMutation,
} from '@services/c2/round.api';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoundConfiguration, RoundModeEnum, RoundStatusEnum } from '@/types/config/round.types';

type Props = {
  uniqueKey: string;
};

export default function ComplexRoundCommand({ uniqueKey }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const monitoringSensor = useAppSelector((state) => selectMonitoringSensorBySensorUniqueKey(state, false, uniqueKey))!;
  const roundConfigurations = useAppSelector(selectActiveRoundConfigurations).filter(
    (roundConfig) => roundConfig.sensorUniqueKey === uniqueKey,
  );
  const onGoingRound = useAppSelector(selectRounds).find((round) => round.sensorUniqueKey === uniqueKey) ?? null;

  const [updateRoundStatus] = useUpdateRoundStatusMutation();
  const [updateRoundSpeed] = useUpdateRoundSpeedMutation();
  const [updateRoundMode] = useUpdateRoundModeMutation();
  const [toggleRoundDirection] = useToggleRoundDirectionMutation();
  const [goToSection] = useGotToSectionMutation();

  const [selectedRoundConfiguration, setSelectedRoundConfiguration] = useState<RoundConfiguration | null>(
    roundConfigurations.find((config) => config.code === onGoingRound?.code) ?? null,
  );
  const [selectedSectionRank, setSelectedSectionRank] = useState<number | null>();
  const roundConfigurationOptions = roundConfigurations.map((roundConfiguration) => ({
    label: roundConfiguration.code,
    value: roundConfiguration.id.toString(),
  }));

  const isCritical = onGoingRound?.mode === RoundModeEnum.CRITICAL;
  const isOngoing = onGoingRound?.status === RoundStatusEnum.ONGOING;
  const isPaused = onGoingRound?.status === RoundStatusEnum.PAUSED;

  const roundSectionOptions =
    selectedRoundConfiguration?.sections
      .toSorted((s1, s2) => s1.rank - s2.rank)
      .map((section) => ({
        label: `Section ${section.rank}`,
        value: section.rank.toString(),
      })) ?? [];

  const isDisabled = selectedRoundConfiguration === null;

  function increaseSpeed(): number {
    const speed = onGoingRound?.speedMultiplier;
    if (!speed) {
      return 0;
    }
    switch (speed) {
      case 1:
        return 2;
      case 2:
        return 4;
      case 4:
        return 6;
      case 6:
      default:
        return 1;
    }
  }

  function onStopClick() {
    if (!isDisabled) {
      const { site, appCode } = monitoringSensor.configuration;
      updateRoundStatus({
        id: selectedRoundConfiguration.id,
        site,
        appCode,
        status: RoundStatusEnum.STOPPED,
      });
    }
  }

  function onSelectedRoundChange(roundId: string) {
    const newSelectedRound =
      roundConfigurations.find((roundConfiguration) => roundConfiguration.id === Number(roundId)) ?? null;
    setSelectedRoundConfiguration(newSelectedRound);
    onStopClick();
  }

  function onSelectedSectionChange(sectionRank: number) {
    if (!isDisabled) {
      const { site, appCode } = monitoringSensor.configuration;
      setSelectedSectionRank(sectionRank);
      goToSection({
        id: selectedRoundConfiguration.id,
        site,
        appCode,
        rank: sectionRank,
      });
    }
  }

  function toggleRoundMode() {
    if (!isDisabled) {
      const { site, appCode } = monitoringSensor.configuration;
      updateRoundMode({
        id: selectedRoundConfiguration.id,
        site,
        appCode,
        mode: isCritical ? RoundModeEnum.NORMAL : RoundModeEnum.CRITICAL,
      });
    }
  }

  function togglePlayPauseRound() {
    if (!isDisabled) {
      const { site, appCode } = monitoringSensor.configuration;
      updateRoundStatus({
        id: selectedRoundConfiguration.id,
        site,
        appCode,
        status: isOngoing ? RoundStatusEnum.PAUSED : RoundStatusEnum.ONGOING,
      });
    }
  }

  function handleUpdateSpeed() {
    if (!isDisabled) {
      const { site, appCode } = monitoringSensor.configuration;
      updateRoundSpeed({
        id: selectedRoundConfiguration.id,
        site,
        appCode,
        speedMultiplier: increaseSpeed(),
      });
    }
  }

  function handleReverseRound() {
    if (!isDisabled) {
      const { site, appCode } = monitoringSensor.configuration;
      toggleRoundDirection({
        id: selectedRoundConfiguration.id,
        site,
        appCode,
      });
    }
  }

  function getSpeedIcon(speed: number) {
    switch (speed) {
      case 1:
        return SpeedX1;
      case 2:
        return SpeedX2;
      case 4:
        return SpeedX4;
      case 6:
        return SpeedX6;
      default:
        return undefined;
    }
  }
  return (
    <VStack width="100%" height="100%" gap={1} backgroundColor="neutral.700" alignItems="flex-start">
      <VStack width="100%" padding={4} alignItems="flex-start">
        <Text fontSize="24px" fontWeight="medium">
          {monitoringSensor.configuration.name}
        </Text>
        <HStack gap={4}>
          <VStack width="200px" gap={1} alignItems="flex-start">
            <Text color="neutral.300">
              <FormattedMessage id="rounds.walkway" />
            </Text>
            <CustomSelect
              width="200px"
              value={selectedRoundConfiguration?.id.toString() ?? ''}
              collection={createListCollection({ items: roundConfigurationOptions })}
              onChange={(value) => value && onSelectedRoundChange(value)}
            />
          </VStack>
          <VStack width="200px" gap={1} alignItems="flex-start">
            <Text color="neutral.300">
              <FormattedMessage id="rounds.section" />
            </Text>
            <CustomSelect
              key={selectedRoundConfiguration?.code}
              width="200px"
              value={selectedSectionRank?.toString() ?? ''}
              collection={createListCollection({ items: roundSectionOptions })}
              onChange={(rankStr) => rankStr && onSelectedSectionChange(Number.parseInt(rankStr))}
            />
          </VStack>
        </HStack>
        <VStack gap={1} alignItems="flex-start">
          <Text color="neutral.300">
            <FormattedMessage id="rounds.mode" />
          </Text>
          <HStack gap={2}>
            <Text color={isCritical ? 'neutral.300' : 'neutral.white'} fontWeight={isCritical ? 'regular' : 'medium'}>
              <FormattedMessage id="rounds.normal" />
            </Text>
            <Switch disabled={isDisabled} size="md" checked={isCritical} onCheckedChange={toggleRoundMode} />
            <Text color={isCritical ? 'neutral.white' : 'neutral.300'} fontWeight={isCritical ? 'medium' : 'regular'}>
              <FormattedMessage id="rounds.critical" />
            </Text>
          </HStack>
        </VStack>
      </VStack>
      <Center width="100%" padding={4} backgroundColor="neutral.900">
        <Icon as={getSpeedIcon(onGoingRound?.speedMultiplier ?? 1)} boxSize="40px" marginRight={3} />
        <Tooltip content={formatMessage({ id: 'rounds.reverse' })}>
          <IconButton
            onClick={handleReverseRound}
            disabled={isDisabled}
            aria-label="backward"
            variant="player"
            boxSize="56px"
          >
            <Icon as={PlayerBackward} color="neutral.300" />
          </IconButton>
        </Tooltip>
        <Tooltip content={formatMessage({ id: !isPaused ? 'rounds.pause' : 'rounds.play' })}>
          <IconButton
            disabled={isDisabled}
            aria-label="backward"
            variant="player"
            boxSize="56px"
            onClick={togglePlayPauseRound}
            data-active={onGoingRound !== null && !isPaused}
          >
            <Icon as={onGoingRound == null || isPaused ? PlayerPlay : PlayerPause} color="neutral.300" />
          </IconButton>
        </Tooltip>
        <Tooltip content={formatMessage({ id: 'rounds.stop' })}>
          <IconButton disabled={isDisabled} aria-label="backward" variant="player" boxSize="56px" onClick={onStopClick}>
            <Icon as={PlayerStop} color="neutral.300" />
          </IconButton>
        </Tooltip>
        <Tooltip content={formatMessage({ id: 'rounds.increaseSpeed' })}>
          <IconButton
            disabled={isDisabled}
            aria-label="backward"
            variant="player"
            boxSize="56px"
            onClick={handleUpdateSpeed}
          >
            <Icon as={PlayerForward} color="neutral.300" />
          </IconButton>
        </Tooltip>
      </Center>
    </VStack>
  );
}
